import { v4 as uuidv4 } from 'uuid'
import { BaseOfflineStore } from './base.store'
import { OfflineConsultation } from '../types/database.types'
import { Consultation, ConsultationType, ConsultationStatus } from '@/core/models/consultation.model'
import { OfflinePatient } from '../types/database.types'
import { queueConsultationForSync, queueConsultationUpdateForSync } from '../utils/sync-queue.utils'

export class ConsultationOfflineStore extends BaseOfflineStore<OfflineConsultation> {
    constructor() {
        super('consultations')
    }

    // Default mesures matching the backend schema
    private getDefaultMesures() {
        return [
            { label: 'Tension', value: null, unit: 'mmHg', order: 1 },
            { label: 'Saturation O2', value: null, unit: '%', order: 2 },
            { label: 'Pouls Périphériques', value: null, unit: 'bpm', order: 3 },
            { label: 'Température', value: null, unit: '°C', order: 4 },
            { label: 'Poids', value: null, unit: 'kg', order: 5 },
            { label: 'Taille', value: null, unit: 'cm', order: 6 },
        ]
    }

    async createOfflineConsultation(patient: OfflinePatient): Promise<OfflineConsultation> {
        const offlineId = uuidv4()
        const now = Date.now()

        const offlineConsultation: OfflineConsultation = {
            _id: offlineId,
            _offlineId: offlineId,
            _localId: offlineId,
            _syncStatus: 'pending',
            _lastModified: now,
            _createdAt: now,
            _patientSyncRequired: patient._syncStatus !== 'synced',
            // Consultation fields
            createdBy: '', // Will be set from current user context
            patient: patient,
            startDate: new Date().toISOString(),
            status: ConsultationStatus.Active,
            type: ConsultationType.Local,
            files: [],
            mesures: this.getDefaultMesures(), // Initialize with default vital signs
            biologicals: [],
            dictaphones: [],
            // Optional fields with defaults
            commentaire: '',
            endDate: undefined,
            establishment: undefined,
            paid: false,
            price: undefined,
            paymentType: undefined,
            validatedAt: undefined,
            receiptUrl: undefined,
            participants: [],
            pds: undefined,
            medecin: undefined,
            notification: undefined,
            transcription: undefined,
            waitingRoom: undefined,
            reason: undefined,
            speciality: undefined,
        }

        const createdConsultation = await this.add(offlineConsultation)

        // Queue for sync
        try {
            await queueConsultationForSync(createdConsultation, patient._id)
        } catch (error) {
            console.error('Failed to queue consultation for sync:', error)
        }

        return createdConsultation
    }

    async getByPatientId(patientId: string): Promise<OfflineConsultation[]> {
        const allConsultations = await this.getAll()
        return allConsultations.filter(consultation => {
            // Handle both string and object patient references
            if (typeof consultation.patient === 'string') {
                return consultation.patient === patientId
            } else {
                return consultation.patient._id === patientId
            }
        })
    }

    async getByOfflineId(offlineId: string): Promise<OfflineConsultation | undefined> {
        const consultations = await this.getByIndex('_offlineId', offlineId)
        return consultations[0]
    }

    async getBySyncStatus(status: 'synced' | 'pending' | 'failed'): Promise<OfflineConsultation[]> {
        return this.getByIndex('_syncStatus', status)
    }

    async updateConsultation(consultationId: string, updates: Partial<OfflineConsultation>): Promise<OfflineConsultation> {
        const consultation = await this.get(consultationId)
        if (!consultation) {
            throw new Error(`Consultation with ID ${consultationId} not found`)
        }

        const updatedConsultation: OfflineConsultation = {
            ...consultation,
            ...updates,
            _lastModified: Date.now(),
            _syncStatus: 'pending', // Mark as needing sync
        }

        const result = await this.update(updatedConsultation)

        // Queue for sync if already synced before (for updates)
        if (consultation._syncStatus === 'synced') {
            try {
                await queueConsultationUpdateForSync(result)
            } catch (error) {
                console.error('Failed to queue consultation update for sync:', error)
            }
        }

        return result
    }

    async endConsultation(consultationId: string): Promise<OfflineConsultation> {
        return this.updateConsultation(consultationId, {
            status: ConsultationStatus.Completed,
            endDate: new Date().toISOString(),
        })
    }

    async addMesures(consultationId: string, mesures: OfflineConsultation['mesures']): Promise<OfflineConsultation> {
        const consultation = await this.get(consultationId)
        if (!consultation) {
            throw new Error(`Consultation with ID ${consultationId} not found`)
        }

        return this.updateConsultation(consultationId, {
            mesures: [...consultation.mesures, ...mesures]
        })
    }

    async addBiological(consultationId: string, biological: OfflineConsultation['biologicals'][0]): Promise<OfflineConsultation> {
        const consultation = await this.get(consultationId)
        if (!consultation) {
            throw new Error(`Consultation with ID ${consultationId} not found`)
        }

        // Add ID to biological if not present
        const biologicalWithId = {
            ...biological,
            id: biological.id || uuidv4()
        }

        return this.updateConsultation(consultationId, {
            biologicals: [...consultation.biologicals, biologicalWithId]
        })
    }

    async removeBiological(consultationId: string, biologicalId: string): Promise<OfflineConsultation> {
        const consultation = await this.get(consultationId)
        if (!consultation) {
            throw new Error(`Consultation with ID ${consultationId} not found`)
        }

        return this.updateConsultation(consultationId, {
            biologicals: consultation.biologicals.filter((bio: any) => bio.id !== biologicalId)
        })
    }

    async markAsSynced(consultation: OfflineConsultation, serverId: string): Promise<OfflineConsultation> {
        const updatedConsultation: OfflineConsultation = {
            ...consultation,
            // Don't change _id as it's the primary key - keep the offline ID
            // _id: serverId, // This line causes the IndexedDB update to fail
            _cloudId: serverId, // Store the server ID separately
            _syncStatus: 'synced',
            _lastModified: Date.now(),
        }

        return this.update(updatedConsultation)
    }

    async getActiveConsultations(): Promise<OfflineConsultation[]> {
        const allConsultations = await this.getAll()
        return allConsultations.filter(consultation =>
            consultation.status === ConsultationStatus.Active ||
            consultation.status === ConsultationStatus.Accepted
        )
    }

    async getConsultationsByType(type: ConsultationType): Promise<OfflineConsultation[]> {
        const allConsultations = await this.getAll()
        return allConsultations.filter(consultation => consultation.type === type)
    }
}

// Create a singleton instance
export const consultationOfflineStore = new ConsultationOfflineStore() 